import {
  ProductVariantAvailabilityStatus,
  SwarovskiOptikSegmentShort,
  VideoType,
  getIsoCodeWithIntFallback,
} from "@swo/shared";
import {
  EnrichedSwarovskiProductVariantFrontend,
  ProductDetailPageDataShared,
  SwarovskiOptikLocaleFrontend,
} from "@swo/storefront";
import { useProductAvailabilityNotificationClient } from "../../../domain/storefront";
import {
  PDPAvailabilityNotificationProductVariantT,
  toPDPAvailabilityNotificationProductVariantT,
} from "./availability/notification/types";
import React, { useCallback, useEffect, useState } from "react";

import { SwarovskiOptikShopModeCapabilities } from "@swo/shared";
import { jsonEq } from "../../../utils/fp/json-eq";
import { PdpData } from "./types";
import { PDPAvailabilityNotificationState } from "./availability/notification/state";
import { PDPAvailabilityNotification } from "./availability/notification/PDPAvailabilityNotification";
import { PDPImagesGridImageT } from "../PDP_Gallery/types";
import { PDPSwarovskiProductVariantFrontend } from "./state/types";
import { ecommerceTrackingAspect } from "../../../domain/google-tag-manager/ecommerce-tracking-aspect";

export function usePDPAvailabilityNotificationState(
  locale: SwarovskiOptikLocaleFrontend,
  segment: SwarovskiOptikSegmentShort,
  translations: PdpData["translations"]["pdp"],
  variantPrimaryImage: PDPImagesGridImageT | undefined,
  variant: PDPSwarovskiProductVariantFrontend,
  availability: ProductVariantAvailabilityStatus,
  privacyPolicyLink: string,
) {
  const [pdpAvailabilityNotificationState, setPdpAvailabilityNotificationState] =
    useState<PDPAvailabilityNotificationState>(getInitialState(variant, availability));

  const [previousVariant, setPreviousVariant] = useState(variant);
  if (!jsonEq(previousVariant, variant)) {
    setPreviousVariant(variant);
    setPdpAvailabilityNotificationState(getInitialState(variant, availability));
  }

  const client = useProductAvailabilityNotificationClient({
    localeCode: locale.code,
    segmentKey: segment.key,
  });

  function getVariantLabel(availability: ProductVariantAvailabilityStatus) {
    return availability.tag === "not-yet-available-for-sale"
      ? translations["availabilityNotificationLabel.notYetAvailableForSale"]
      : translations["availabilityNotificationLabel.outOfStock"];
  }

  function getInitialState(
    variant: PDPSwarovskiProductVariantFrontend,
    availability: ProductVariantAvailabilityStatus,
  ): PDPAvailabilityNotificationState {
    if (locale.shopMode.availabilitySubscription === "disabled") {
      return { tag: "impossible" };
    }

    const doesHaveAvailabilityNotification = canHaveAvailabilityNotification({
      mode: locale.shopMode.availabilitySubscription,
      availability: availability,
      backInStockNotificationsEnabled: variant.backInStockNotificationsEnabled,
    });

    if (!doesHaveAvailabilityNotification) {
      return { tag: "disabled" };
    }

    const variantAdapter = toPDPAvailabilityNotificationProductVariantT(variant, availability, variantPrimaryImage);
    const variantLabel = getVariantLabel(variantAdapter.availability);

    return {
      tag: "closed",
      variant: variantAdapter,
      label: variantLabel.value,
      openSignUpScreen: () => {
        setPdpAvailabilityNotificationState({
          tag: "sign-up",
          label: variantLabel.value,
          variant: variantAdapter,
          onAvailabilitySubscription: onAvailabilitySubscriptionCb(variantAdapter),
        });

        return Promise.resolve();
      },
    };
  }

  const onAvailabilitySubscriptionCb = (v: PDPAvailabilityNotificationProductVariantT) => {
    return (email: string) => {
      const { code, country, language } = locale;

      return client
        .subscribe({ email: email, variantSku: v.sku })
        .then(() => {
          setPdpAvailabilityNotificationState({ tag: "thank-you" });
          ecommerceTrackingAspect
            .productAvailabilitySubscription(
              code,
              language.isoCode,
              getIsoCodeWithIntFallback(country),
              segment.key,
              email,
              v.sku,
            )
            .push();
        })
        .catch(e => {
          console.error(e);

          setPdpAvailabilityNotificationState(getInitialState(variant, availability));
        });
    };
  };

  const hideModalCallback = useCallback(() => {
    setPdpAvailabilityNotificationState(getInitialState(variant, availability));
  }, [variant]);

  const AvailabilityNotification = (
    <PDPAvailabilityNotification
      locale={locale}
      translations={translations}
      privacyPolicyLink={privacyPolicyLink}
      screen={pdpAvailabilityNotificationState}
      hideModalCallback={hideModalCallback}
    />
  );

  return {
    pdpAvailabilityNotificationState,
    AvailabilityNotification,
  };
}

function canHaveAvailabilityNotification({
  availability,
  backInStockNotificationsEnabled,
  mode,
}: {
  mode: Exclude<SwarovskiOptikShopModeCapabilities["availabilitySubscription"], "disabled">;
  availability: ProductVariantAvailabilityStatus;
  backInStockNotificationsEnabled: boolean;
}): boolean {
  switch (mode) {
    case "enabled": {
      return (
        (availability.tag === "out-of-stock" || availability.tag === "not-yet-available-for-sale") &&
        backInStockNotificationsEnabled
      );
    }

    case "force-enabled": {
      return true;
    }

    default: {
      const _: never = mode;

      return _;
    }
  }
}
